import { useEffect } from 'react';

import { Button } from '@mui/material';
import Container from '@mui/material/Container';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { getEmployeeById } from 'src/redux/slices/employees';

import ErrorContent from 'src/components/error-content';
import { useSettingsContext } from 'src/components/settings';
import { LeftIcon } from 'src/components/carousel/arrow-icons';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';

import EmployeeDetails from '../employee-details';
import { LoadingScreen } from '../../../components/loading-screen';
import { useAppDispatcher, useAppSelector } from '../../../redux/store';

type Props = {
  id: string;
};

export default function EmployeeDetailView({ id }: Props) {
  const { employee, status, error } = useAppSelector((state) => state.employees);

  const settings = useSettingsContext();
  const dispatch = useAppDispatcher();

  useEffect(() => {
    dispatch(getEmployeeById({ employeeId: id }));
  }, [dispatch, id]);

  if (status === 'loading' || status === 'idle') {
    return <LoadingScreen />;
  }

  if (status === 'failure') {
    return (
      <ErrorContent
        filled
        title="Failed to load employee details"
        description={error}
        action={
          <Button
            component={RouterLink}
            href={paths.dashboard.employees.root}
            variant="contained"
            startIcon={<LeftIcon />}
          >
            Back to Employees
          </Button>
        }
        sx={{
          py: 10,
        }}
      />
    );
  }

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Employee Details"
        links={[
          {
            name: 'Employees',
            href: paths.dashboard.employees.root,
          },
          { name: 'Employee Details' },
        ]}
        sx={{
          mb: { xs: 3, md: 5 },
        }}
      />

      <EmployeeDetails currentEmployee={employee} />
    </Container>
  );
}
